import axios from 'axios';

// services/lei/leiGleifSearch.ts
// Public GLEIF API - plain axios on purpose (no app instance, so no auth headers / interceptors are sent to a 3rd party).

const GLEIF_API_URL = 'https://api.gleif.org/api/v1';

/** GLEIF does not serve pages beyond the first 10 000 records of one query. */
export const GLEIF_MAX_RECORDS = 10000;

/**
 * Filter used for the "name" search.
 * - 'fulltext'          -> matches parts of the name (and other names), what users expect from a search box
 * - 'entity.legalName'  -> only matches the exact legal name
 * Switch the value here if exact matching is wanted.
 */
const NAME_FILTER = 'fulltext';

/** table column key -> GLEIF `sort` field. Add a column here to make it sortable on the server. */
export const LEI_SORT_FIELDS: Record<string, string> = {
    lei: 'lei',
    legalName: 'entity.legalName',
    registeredAs: 'entity.registeredAs',
    country: 'entity.legalAddress.country',
    registrationStatus: 'registration.status',
    nextRenewalDate: 'registration.nextRenewalDate'
};

export type TLeiSearchType = 'lei' | 'ico' | 'name';

export interface ILeiSearchFilters {
    q: string;
    jurisdikce: string[];
    kategorie: string[];
    stavLei: string[];
}

export interface ILeiSearchSort {
    columnKey: string;
    order: 'ascend' | 'descend';
}

export interface ILeiSearchRow {
    lei: string;
    legalName: string;
    registeredAs: string;
    country: string;
    registrationStatus: string;
    lastUpdateDate?: string;
    nextRenewalDate?: string;
    /** GLEIF `entity.jurisdiction` - the accredited jurisdiction (country) of the LEI */
    jurisdiction?: string;
    /** GLEIF `registration.managingLou` - the LOU that currently manages the LEI */
    managingLou?: string;
}

export interface ILeiSearchResult {
    rows: ILeiSearchRow[];
    total: number;
}

interface IGleifRecord {
    id: string;
    attributes: {
        lei: string;
        entity?: {
            legalName?: { name?: string };
            registeredAs?: string | null;
            legalAddress?: { country?: string };
            jurisdiction?: string;
        };
        registration?: {
            status?: string;
            lastUpdateDate?: string;
            nextRenewalDate?: string;
            managingLou?: string;
        };
    };
}

interface IGleifResponse {
    data: IGleifRecord[];
    meta?: { pagination?: { total?: number } };
}

export const isLeiSearchCancelled = axios.isCancel;

/** 20 alphanumeric characters = LEI, 6+ digits = registration number (IČO), anything else = company name */
export const detectLeiSearchType = (q: string): TLeiSearchType => {
    if (/^[A-Z0-9]{20}$/i.test(q)) return 'lei';
    if (/^\d{6,}$/.test(q)) return 'ico';
    return 'name';
};

const buildParams = (
    filters: ILeiSearchFilters,
    page: number,
    pageSize: number,
    sort?: ILeiSearchSort
): Record<string, string | number> => {
    const params: Record<string, string | number> = {
        'page[number]': page,
        'page[size]': pageSize
    };

    const q = filters.q.trim();
    const type = q ? detectLeiSearchType(q) : undefined;

    if (type === 'lei') {
        // an LEI is unique, so the other filters are ignored (otherwise a foreign LEI would never be found with CZ preselected)
        params['filter[lei]'] = q.toUpperCase();
    } else {
        if (type === 'ico') params['filter[entity.registeredAs]'] = q;
        if (type === 'name') params[`filter[${NAME_FILTER}]`] = q;
        // several values in one filter are separated by a comma (= "IN")
        if (filters.jurisdikce.length) params['filter[entity.jurisdiction]'] = filters.jurisdikce.join(',');
        if (filters.kategorie.length) params['filter[entity.category]'] = filters.kategorie.join(',');
        if (filters.stavLei.length) params['filter[registration.status]'] = filters.stavLei.join(',');
    }

    const sortField = sort && LEI_SORT_FIELDS[sort.columnKey];
    if (sort && sortField) params.sort = `${sort.order === 'descend' ? '-' : ''}${sortField}`;

    return params;
};

const mapRecord = ({ id, attributes }: IGleifRecord): ILeiSearchRow => ({
    lei: attributes.lei || id,
    legalName: attributes.entity?.legalName?.name || '',
    registeredAs: attributes.entity?.registeredAs || '',
    country: attributes.entity?.legalAddress?.country || '',
    registrationStatus: attributes.registration?.status || '',
    lastUpdateDate: attributes.registration?.lastUpdateDate,
    nextRenewalDate: attributes.registration?.nextRenewalDate,
    jurisdiction: attributes.entity?.jurisdiction,
    managingLou: attributes.registration?.managingLou
});

export const searchLeiRecords = async (
    filters: ILeiSearchFilters,
    page: number,
    pageSize: number,
    sort?: ILeiSearchSort,
    signal?: AbortSignal
): Promise<ILeiSearchResult> => {
    const { data } = await axios.get<IGleifResponse>(`${GLEIF_API_URL}/lei-records`, {
        params: buildParams(filters, page, pageSize, sort),
        signal
    });

    return {
        rows: (data.data || []).map(mapRecord),
        total: data.meta?.pagination?.total ?? 0
    };
};
